import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  GridModule,
  FreezeService,
  ResizeService,
  SearchService,
  ToolbarService,
  PageService,
  SortService,
  FilterService,
  GroupService,
  ExcelExportService,
  RecordDoubleClickEventArgs,
} from '@syncfusion/ej2-angular-grids';
import {
  BaseTableView,
  IListRequestField,
  IRangeFilterColumnConfig,
  O3GridToolbar,
  O3GridNoData,
  O3TableHeader,
  TranslatePipe,
} from 'O3_core';
import { firstValueFrom } from 'rxjs';
import { ISmsQueue } from '../interfaces/sms-queue.interface';
import { ISmsStatus } from '../interfaces/sms-status.interface';
import { SmsQueueService } from '../services/sms-queue.service';
import { SmsStatusService } from '../services/sms-status.service';
import { SMS_QUEUE_RESOURCE } from '../constants/sms-queue.constants';

@Component({
  selector: 'o3-notifications-sms-queue-list',
  imports: [
    CommonModule,
    TranslatePipe,
    O3TableHeader,
    GridModule,
    O3GridToolbar,
    O3GridNoData,
  ],
  providers: [
    PageService,
    SortService,
    FilterService,
    GroupService,
    SearchService,
    ToolbarService,
    ExcelExportService,
    FreezeService,
    ResizeService,
  ],
  templateUrl: './list.html',
  styleUrl: './list.css',
})
export class SmsQueueList extends BaseTableView<ISmsQueue> implements OnInit {
  dataService = inject(SmsQueueService);
  private readonly smsStatusService = inject(SmsStatusService);

  statusMap = signal<Map<string, ISmsStatus>>(new Map());

  breadCrumbs: string[] = ['breadCrumbs.notifications', 'breadCrumbs.smsQueue'];

  kcResource = SMS_QUEUE_RESOURCE;

  listRequestFields: IListRequestField[] = [
    { name: 'status', type: 'string' },
    { name: 'recipientPhone', type: 'string', isSearchable: true },
    { name: 'messageContent', type: 'string', isSearchable: true },
    { name: 'sendItOn', type: 'date' },
    { name: 'sentAt', type: 'date' },
    { name: 'retryCount', type: 'number' },
    { name: 'failureReason', type: 'string' },
    { name: 'tenantId', type: 'string' },
  ];

  override rangeColumnFilters: IRangeFilterColumnConfig[] = [
    { field: 'sendItOn', kind: 'datetime' },
    { field: 'sentAt', kind: 'datetime' },
    { field: 'createdAt', kind: 'datetime' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadStatusCatalog();
  }

  private async loadStatusCatalog(): Promise<void> {
    try {
      const result = (await firstValueFrom(this.smsStatusService.list({}))) as {
        items: ISmsStatus[];
      };
      const map = new Map<string, ISmsStatus>();
      (result?.items ?? []).forEach((status) => map.set(status.code, status));
      this.statusMap.set(map);
    } catch (error) {
      this.processError(error);
    }
  }

  getStatusColor(statusCode: string): string {
    return this.statusMap().get(statusCode)?.bgColor || '#f0f0f0';
  }

  getStatusFgColor(statusCode: string): string {
    return this.statusMap().get(statusCode)?.fgColor || '#333';
  }

  getStatusName(statusCode: string): string {
    return this.statusMap().get(statusCode)?.name || statusCode;
  }

  override editRecord(data: ISmsQueue): void {
    if (data?.id) {
      this.router.navigate(['../', data.id], {
        relativeTo: this.activatedRoute,
      });
    }
  }

  override recordDoubleClick(event: RecordDoubleClickEventArgs): void {
    const sms = event.rowData as ISmsQueue | undefined;
    if (sms) this.editRecord(sms);
  }
}
